/**
 * LoadTimeAccounting
 *
 * Responsible for displaying the elapsed times of the current automation cycle:
 * - Total time
 * - Loading time
 * - Pause time
 * - Manual time
 *
 * General flow:
 * 1. The current automation status is received through the `automationStatus` prop,
 *    avoiding continuous polling for the current status inside this component.
 * 2. Whenever the status changes, the component appends the new event to the local
 *    history (`statusList`) or resets the timers when the status returns to `ON`.
 * 3. If the cycle has already started but the local history is not enough to rebuild
 *    the timeline correctly, the component fetches `LIST_AUTOMATION_STATUS_QUERY`
 *    to retrieve the full status history.
 * 4. The cycle start is defined as the first `RUNNING`/`BOOSTED` status after the
 *    most recent `ON`.
 * 5. From that point on, elapsed time is accumulated by category:
 *    - LOAD: RUNNING / BOOSTED after "equipment ready"
 *    - PAUSE: PAUSED
 *    - MANUAL: MANUAL / MAINTENANCE
 *    - OTHER: ON / OFF / ERROR / any other states
 *
 * Notes:
 * - The component reads `automationStatus.currentAutomationStatus` because the prop
 *   is received wrapped in that shape.
 * - `currentStatusEventKey` is used to reliably detect real event changes
 *   (`id`, `insertedAt`, `status`).
 * - The visual clock updates every 250ms only to refresh the displayed elapsed time;
 *   the current status itself comes from the parent component.
 */


import React from "react";
import styled from "styled-components";

import { Query } from "react-apollo";
import { LIST_AUTOMATION_STATUS_QUERY } from "../../views/AutomationStatus/automationStatusQueries";

const pad2 = (n) => String(n).padStart(2, "0");

const formatMs = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const hh = Math.floor(totalSeconds / 3600);
  const mm = Math.floor((totalSeconds % 3600) / 60);
  const ss = totalSeconds % 60;
  return hh > 0 ? `${pad2(hh)}:${pad2(mm)}:${pad2(ss)}` : `${pad2(mm)}:${pad2(ss)}`;
};

const BUCKET = {
  LOAD: "load",
  PAUSE: "pause",
  MANUAL: "manual",
  OTHER: "other",
};

const isEquipmentReady = (description) => {
  const d = (description || "").toLowerCase();
  return d.includes("equipamento pronto");
};

const isOnStatus = (status) => ((status || "").toUpperCase() === "ON");

const computeCycleStartMs = (eventsAsc) => {
  if (!eventsAsc || !eventsAsc.length) return null;

  let lastOnIdx = -1;
  for (let i = eventsAsc.length - 1; i >= 0; i -= 1) {
    if (isOnStatus(eventsAsc[i].status)) {
      lastOnIdx = i;
      break;
    }
  }

  const tail = lastOnIdx >= 0 ? eventsAsc.slice(lastOnIdx + 1) : eventsAsc;
  if (!tail.length) return null;

  for (let i = 0; i < tail.length; i += 1) {
    const s = (tail[i].status || "").toUpperCase();
    if (s === "RUNNING" || s === "BOOSTED") return tail[i].t;
  }

  return null;
};

const eventMs = (cur) => {
  const iso = cur && cur.insertedAt ? cur.insertedAt : null;
  const ms = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(ms) ? ms : null;
};

const statusToBucket = (status, readySeen) => {
  const s = (status || "").toUpperCase();

  switch (s) {
    case "RUNNING":
    case "BOOSTED":
      return readySeen ? BUCKET.LOAD : BUCKET.OTHER;

    case "PAUSED":
      return BUCKET.PAUSE;

    case "MANUAL":
    case "MAINTENANCE":
      return BUCKET.MANUAL;

    case "ERROR":
    case "ON":
    case "OFF":
    default:
      return BUCKET.OTHER;
  }
};

const nowMs = () => Date.now();

const normalizeList = (list) => {
  const items = Array.isArray(list) ? list : [];
  return items
    .map((e) => ({
      t: eventMs(e),
      status: e ? e.status : null,
      description: e ? e.description : "",
    }))
    .filter((e) => e.t !== null)
    .sort((a, b) => a.t - b.t);
};

const computeTotalsFromHistory = (eventsAsc, cycleStartAt, now) => {
  const totals = {};
  totals[BUCKET.LOAD] = 0;
  totals[BUCKET.PAUSE] = 0;
  totals[BUCKET.MANUAL] = 0;
  totals[BUCKET.OTHER] = 0;

  if (!eventsAsc.length || !cycleStartAt) return totals;

  const filtered = eventsAsc.filter((e) => e.t >= cycleStartAt);
  if (!filtered.length) return totals;

  let readySeen = false;

  for (let i = 0; i < filtered.length; i += 1) {
    const cur = filtered[i];
    const next = filtered[i + 1] || null;

    if (isEquipmentReady(cur.description)) readySeen = true;

    const from = cur.t;
    const to = next ? next.t : now;

    if (to > from) {
      const bucket = statusToBucket(cur.status, readySeen);
      totals[bucket] = (totals[bucket] || 0) + (to - from);
    }
  }

  return totals;
};

const statusKey = (s) => (s ? `${s.id || ""}|${s.insertedAt || ""}|${s.status || ""}` : "");

class HistoryListBridge extends React.PureComponent {
  componentDidMount() {
    const { list, onChange } = this.props;
    onChange(list);
  }

  componentDidUpdate(prevProps) {
    const prevHead = Array.isArray(prevProps.list) && prevProps.list.length ? prevProps.list[0] : null;
    const curHead = Array.isArray(this.props.list) && this.props.list.length ? this.props.list[0] : null;

    const prevKey = prevHead
      ? `${prevHead.id || ""}|${prevHead.insertedAt || ""}|${prevHead.status || ""}`
      : "";

    const curKey = curHead
      ? `${curHead.id || ""}|${curHead.insertedAt || ""}|${curHead.status || ""}`
      : "";

    if (prevKey !== curKey) {
      this.props.onChange(this.props.list);
    }
  }

  render() {
    return null;
  }
}

export default function LoadTimeAccounting({ automationStatus }) {
  const listRefetchRef = React.useRef(null);
  const lastCurKeyRef = React.useRef(null);
  const [listEnabled, setListEnabled] = React.useState(false);
  const lastListFetchAtRef = React.useRef(0);
  const [uiNow, setUiNow] = React.useState(nowMs());
  React.useEffect(() => {
    const t = setInterval(() => setUiNow(nowMs()), 250);
    return () => {
      clearInterval(t);
    };
  }, []);

  const [statusList, setStatusList] = React.useState([]);
  const currentStatus = automationStatus && automationStatus.currentAutomationStatus
    ? automationStatus.currentAutomationStatus
    : null;

  const currentStatusEventKey = currentStatus
    ? `${currentStatus.id || ""}|${currentStatus.insertedAt || ""}|${currentStatus.status || ""}`
    : "";

  const resetTimers = React.useCallback((cur) => {
    setStatusList(cur ? [cur] : []);
  }, []);

  const seedFromList = React.useCallback((incoming) => {
    const next = Array.isArray(incoming) ? incoming : [];
    setStatusList((prev) => {
      const prevItems = Array.isArray(prev) ? prev : [];

      if (!prevItems.length) {
        return next;
      }

      const prev0 = prevItems[0];
      const next0 = next[0];
      const prevKey = prev0 ? `${prev0.id || ""}|${prev0.insertedAt || ""}` : "";
      const nextKey = next0 ? `${next0.id || ""}|${next0.insertedAt || ""}` : "";

      if (prevItems.length !== next.length) {
        return next;
      }
      if (prevKey !== nextKey) {
        return next;
      }
      return prevItems;
    });
  }, []);

  const appendCurrent = React.useCallback((cur) => {
    if (!cur) return;
    const key = `${cur.id || ""}|${cur.insertedAt || ""}`;
    setStatusList((prev) => {
      const items = Array.isArray(prev) ? prev : [];
      for (let i = 0; i < items.length; i += 1) {
        const it = items[i];
        const itKey = `${it && it.id ? it.id : ""}|${it && it.insertedAt ? it.insertedAt : ""}`;
        if (itKey === key) {
          return items;
        }
      }
      return [cur, ...items];
    });
  }, []);

  const handleCurrentStatus = React.useCallback((cur, cycleStartAtValue, eventsAscLength) => {
    if (!cur) return;

    const curStatusUpper = (cur.status || "").toUpperCase();
    const isOn = curStatusUpper === "ON";
    const curKey = statusKey(cur);
    const isNewEvent = lastCurKeyRef.current !== curKey;

    if (isNewEvent) {
      lastCurKeyRef.current = curKey;

      if (isOn) {
        resetTimers(cur);
      } else {
        appendCurrent(cur);
      }
    }

    if (!isOn && !listEnabled) {
      setListEnabled(true);
    }

    if (!isOn) {
      const now = Date.now();
      const shouldRefetchList = (!cycleStartAtValue || !eventsAscLength);

      if (shouldRefetchList && listRefetchRef.current && (now - lastListFetchAtRef.current > 2000)) {
        lastListFetchAtRef.current = now;
        listRefetchRef.current();
      }
    }
  }, [appendCurrent, listEnabled, resetTimers]);

  const eventsAsc = React.useMemo(() => normalizeList(statusList), [statusList]);
  const cycleStartAt = React.useMemo(() => computeCycleStartMs(eventsAsc), [eventsAsc]);

  React.useEffect(() => {
    handleCurrentStatus(currentStatus, cycleStartAt, eventsAsc.length);
  }, [currentStatusEventKey, cycleStartAt, eventsAsc.length, handleCurrentStatus]);

  const totalMs = React.useMemo(
    () => (cycleStartAt ? Math.max(0, uiNow - cycleStartAt) : 0),
    [uiNow, cycleStartAt]
  );

  const totals = React.useMemo(
    () => computeTotalsFromHistory(eventsAsc, cycleStartAt, uiNow),
    [eventsAsc, cycleStartAt, uiNow]
  );


  const rows = React.useMemo(
    () => ([
      { title: "Tempo total", value: totalMs },
      { title: "Carregamento", value: totals[BUCKET.LOAD] || 0 },
      { title: "Pausa", value: totals[BUCKET.PAUSE] || 0 },
      { title: "Manual", value: totals[BUCKET.MANUAL] || 0 },
    ]),
    [totalMs, totals]
  );

  return (
    <Wrap>
      {listEnabled ? (
        <Query
          query={LIST_AUTOMATION_STATUS_QUERY}
          pollInterval={0}
          fetchPolicy="network-only"
          notifyOnNetworkStatusChange
          errorPolicy="all"
        >
          {({ data, refetch }) => {
            listRefetchRef.current = refetch;
            const next = data && data.listAutomationStatus ? data.listAutomationStatus : [];
            return <HistoryListBridge list={next} onChange={seedFromList} />;
          }}
        </Query>
      ) : null}

      <List>
        {rows.map((r) => (
          <Row key={r.title}>
            <Title>{r.title}</Title>
            <Pill>{formatMs(r.value)}</Pill>
          </Row>
        ))}
      </List>
    </Wrap>
  );
}

const FONT_FAMILY = '"Rubik", sans-serif';

const Wrap = styled.div`
  font-family: ${FONT_FAMILY};
  color: #2e384d;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 4px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  width: 100%;
  justify-content: space-between;

  padding: 8px 0px;

  & + & {
    border-top: 2px solid #f1f4fe;
  }
`;

const Title = styled.div`
  font-size: 15px;
  font-weight: 500;
  line-height: 21px;
  color: #2e384d;

  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;

  user-select: none;
`;

const Pill = styled.div`
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;

  color: #2e384d;
  font-variant-numeric: tabular-nums;

  min-width: 76px;
  text-align: center;

  padding: 6px 10px;
  border-radius: 999px;

  border: 1px solid rgba(46, 56, 77, 0.12);
  background: rgba(246, 248, 255, 0.9);
`;
